import { useState } from "react";
import { fmt } from "../utils/fmt";
import { DEBRIS_ITEMS, BASE_MATERIALS, TIER1_MATERIALS, TIER2_MATERIALS, TIER3_MATERIALS, DISCOVERABLE_MATS } from "../data/items";
import { SMELT_RECIPES } from "../data/recipes";

// ── Helpers ───────────────────────────────────────────────────────────────────

// Given a recipe input, figure out where the item comes from (raw inventory or
// smelted materials) and how many the player currently has.
function resolveInput(inp, inventory, materials) {
  const raw = DEBRIS_ITEMS.find(d => d.id === inp.id);
  if (raw) return { meta: raw, have: inventory[inp.id] || 0 };
  const mat = BASE_MATERIALS.find(m => m.id === inp.id);
  return { meta: mat ?? { icon: "?", color: "#aaa" }, have: materials[inp.id] || 0 };
}

// How many full runs of a recipe the player can afford right now,
// taking the cost multiplier into account (costMult < 1 means cheaper).
function maxAffordable(recipe, inventory, materials, costMult = 1) {
  let runs = Infinity;
  for (const inp of recipe.inputs) {
    const { have } = resolveInput(inp, inventory, materials);
    const effectiveCost = Math.ceil(inp.qty * costMult);
    runs = Math.min(runs, Math.floor(have / effectiveCost));
  }
  return runs === Infinity ? 0 : runs;
}

// Map a recipe to a numbered display tier.
function recipeGroup(r) {
  if (r.unlockResearch === "refinery5") return 4;
  if (r.unlockMat) {
    const advancedMats = ["core", "filament"];
    return advancedMats.includes(r.unlockMat) ? 3 : 2;
  }
  return 1;
}

const TIER_LABELS = {
  1: "BASIC FORGE",
  2: "DISCOVERED FORGE",
  3: "ADVANCED ALLOYS",
  4: "EXOTIC FORGE LAB",
};
const TIER_COLORS = {
  1: "#ff9944",
  2: "#aa55ff",
  3: "#44bbff",
  4: "#ff5588",
};

// ── Sub-components ────────────────────────────────────────────────────────────

// A single compact recipe card (one of three per row).
// Shows recipe name, output, ingredients, and time in a tight vertical layout.
function RecipeCard({ recipe, inventory, materials, activeBatch, queueFull, onQueue, costMult, yieldMult, discoveredMaterials }) {
  // A recipe with unlockMat is "locked" until the player has discovered that material
  const matLocked = recipe.unlockMat && !(discoveredMaterials?.has(recipe.unlockMat));

  const affordable  = matLocked ? 0 : maxAffordable(recipe, inventory, materials, costMult);
  const batchCount  = activeBatch === "max" ? affordable : activeBatch;
  const canAfford   = affordable >= batchCount && batchCount >= 1;
  const clickable   = canAfford && !queueFull && !matLocked;

  const isFuelRecipe = recipe.outputType === "fuel" || recipe.output === "fuel";
  const om           = isFuelRecipe
    ? { icon: "⛽", color: "#ffd54f", name: "Fuel" }
    : BASE_MATERIALS.find(m => m.id === recipe.output);
  const totalOutput = Math.round(batchCount * recipe.outputQty * yieldMult);
  const totalTime   = batchCount * recipe.time;

  if (matLocked) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", textAlign: "left", padding: 0,
        background: "rgba(255,255,255,0.01)", border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 9, overflow: "hidden", minWidth: 0, opacity: 0.55,
      }}>
        <div style={{ padding: "8px 8px 6px", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <span style={{ fontSize: 20 }}>🔒</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.2)" }}>×?</span>
          <span style={{ fontSize: 9, fontWeight: 700, color: "#445566", letterSpacing: 0.3, textAlign: "center", lineHeight: 1.2 }}>???</span>
        </div>
        <div style={{ padding: "6px 6px 7px", display: "flex", flexDirection: "column", gap: 3 }}>
          {recipe.inputs.map((inp, ii) => {
            const isUnknownMat = inp.id === recipe.unlockMat;
            const { meta, have } = isUnknownMat ? { meta: { icon: "❓", color: "#445566" }, have: 0 } : resolveInput(inp, inventory, materials);
            return (
              <div key={ii} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10 }}>
                <span style={{ fontSize: 11 }}>{meta.icon}</span>
                <span style={{ fontWeight: 700, color: "#445566" }}>{isUnknownMat ? "?" : fmt(have)}</span>
                <span style={{ color: "rgba(255,255,255,0.15)" }}>/{isUnknownMat ? "?" : fmt(Math.ceil(inp.qty * costMult))}</span>
              </div>
            );
          })}
          <div style={{ marginTop: 2, fontSize: 9, color: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", gap: 2 }}>
            <span>⏱</span><span>?s</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => clickable && onQueue(recipe)}
      disabled={!clickable}
      style={{
        display: "flex", flexDirection: "column", textAlign: "left", padding: 0,
        background: clickable ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.01)",
        border: `1px solid ${clickable ? "rgba(255,160,60,0.28)" : "rgba(255,255,255,0.06)"}`,
        borderRadius: 9, cursor: clickable ? "pointer" : "default", fontFamily: "inherit",
        color: "#ddeeff", opacity: clickable ? 1 : 0.38,
        transition: "border-color 0.15s, background 0.15s", overflow: "hidden", minWidth: 0,
      }}
    >
      <div style={{ padding: "8px 8px 6px", borderBottom: "1px solid rgba(255,255,255,0.05)", background: clickable ? "rgba(255,140,40,0.05)" : "transparent", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
        <span style={{ fontSize: 20 }}>{om?.icon}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: om?.color }}>×{totalOutput}</span>
        <span style={{ fontSize: 9, fontWeight: 700, color: clickable ? "rgba(255,200,120,0.85)" : "#556677", letterSpacing: 0.3, textAlign: "center", lineHeight: 1.2 }}>
          {recipe.name}
        </span>
      </div>
      <div style={{ padding: "6px 6px 7px", display: "flex", flexDirection: "column", gap: 3 }}>
        {recipe.inputs.map((inp, ii) => {
          const { meta, have } = resolveInput(inp, inventory, materials);
          const need   = Math.ceil(inp.qty * costMult) * batchCount;
          const enough = have >= need;
          return (
            <div key={ii} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10 }}>
              <span style={{ fontSize: 11 }}>{meta.icon}</span>
              <span style={{ fontWeight: 700, color: enough ? meta.color : "#ff5555" }}>{fmt(have)}</span>
              <span style={{ color: "rgba(255,255,255,0.25)" }}>/{fmt(need)}</span>
            </div>
          );
        })}
        <div style={{ marginTop: 2, fontSize: 9, color: "rgba(255,255,255,0.3)", display: "flex", alignItems: "center", gap: 2 }}>
          <span>⏱</span><span>{totalTime}s</span>
        </div>
      </div>
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function SmelterTab({
  inventory, materials, researchUnlocks, stats, discoveredMaterials = new Set(),
  smeltQueue, smeltProgress, smeltRunsDone,
  queueSmelt, removeFromQueue, queueMax,
}) {
  // Currently selected batch multiplier (1 / 10 / 500 / "max")
  const [batchSize, setBatchSize] = useState(1);

  const queueFull = smeltQueue.length >= (queueMax ?? 1);

  // Read forge modifier stats — default to neutral values if not yet researched
  const costMult  = Math.max(0.1, stats?.smeltCostMult  ?? 1);
  const yieldMult = Math.max(1,   stats?.smeltYield     ?? 1);

  // Gate: smelter requires the first refinery level to be researched
  if (!researchUnlocks.has("refinery1")) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, color: "rgba(255,160,80,0.6)", fontSize: 13, padding: 32, textAlign: "center" }}>
        <span style={{ fontSize: 32 }}>🔒</span>
        <div style={{ fontWeight: 700, letterSpacing: 1 }}>SMELTER LOCKED</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>Research Basic Refinery to unlock the smelter.</div>
      </div>
    );
  }

  const visibleRecipes = SMELT_RECIPES.filter(r => {
    if (r.unlockResearch && !researchUnlocks.has(r.unlockResearch)) return false;
    return true;
  });

  // Collect the unique tier groups present in the visible set, sorted ascending
  const groups = [...new Set(visibleRecipes.map(recipeGroup))].sort((a, b) => a - b);

  // Build the list of batch size options the player has currently unlocked.
  // 1× is always available; the rest require specific research unlock strings.
  const batchOptions = [
    { label: "1×",    value: 1,     always: true },
    { label: "10×",   value: 10,    unlock: "batch_10" },
    { label: "500×",  value: 500,   unlock: "batch_500" },
    { label: "MAX",   value: "max", unlock: "batch_max" },
  ].filter(o => o.always || researchUnlocks.has(o.unlock));

  // If the player somehow has a batch size selected that's no longer valid
  // (e.g. save from a different version), fall back to 1×.
  const validValues  = batchOptions.map(o => o.value);
  const activeBatch  = validValues.includes(batchSize) ? batchSize : 1;

  // Queue handler: resolve "max" to an actual number, then call the hook
  function handleQueue(recipe) {
    const count = activeBatch === "max"
      ? maxAffordable(recipe, inventory, materials, costMult)
      : activeBatch;
    if (count < 1) return;
    queueSmelt(recipe.id, count);
  }

  // Data for the currently active (front-of-queue) slot
  const front      = smeltQueue[0];
  const frontRec   = front ? SMELT_RECIPES.find(r => r.id === front.recipeId) : null;
  const totalRuns  = front?.count ?? 1;
  const runsDone   = front?.runsDone ?? smeltRunsDone ?? 0;
  const isBatch    = totalRuns > 1;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* ── Inventory strip: raw salvage + smelted materials ── */}
      <div style={{ padding: "12px 14px 6px", flexShrink: 0 }}>
        <div style={{ fontSize: 11, letterSpacing: 1, color: "rgba(255,160,80,0.7)", marginBottom: 6, fontWeight: 700 }}>REFINERY CONSOLE</div>

        {/* Starter base mats */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", marginBottom: 4, padding: "7px 9px", background: "rgba(255,255,255,0.02)", borderRadius: 6, border: "1px solid rgba(255,255,255,0.06)" }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,160,80,0.5)", letterSpacing: 0.8, alignSelf: "center", marginRight: 2 }}>BASE</span>
          {DEBRIS_ITEMS.filter(d => d.category === "starter").map(di => (
            <div key={di.id} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 12, opacity: (inventory[di.id] || 0) > 0 ? 1 : 0.25 }}>
              <span style={{ fontSize: 13 }}>{di.icon}</span>
              <span style={{ color: di.color, fontWeight: 600 }}>{fmt(inventory[di.id] || 0)}</span>
            </div>
          ))}
        </div>

        {/* Discovered base mats — only shown once at least one is found */}
        {DISCOVERABLE_MATS.some(d => discoveredMaterials.has(d.id)) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", marginBottom: 4, padding: "7px 9px", background: "rgba(170,85,255,0.04)", borderRadius: 6, border: "1px solid rgba(170,85,255,0.18)" }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: "rgba(200,140,255,0.6)", letterSpacing: 0.8, alignSelf: "center", marginRight: 2 }}>DISC</span>
            {DISCOVERABLE_MATS.filter(d => discoveredMaterials.has(d.id)).map(di => (
              <div key={di.id} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 12, opacity: (inventory[di.id] || 0) > 0 ? 1 : 0.25 }}>
                <span style={{ fontSize: 13 }}>{di.icon}</span>
                <span style={{ color: di.color, fontWeight: 600 }}>{fmt(inventory[di.id] || 0)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Smelted material tiers — only render tiers that have content */}
        {[TIER1_MATERIALS, TIER2_MATERIALS, TIER3_MATERIALS].map((tierMats, ti) => {
          const relevant = tierMats.filter(m => m.tier === 1 || (materials[m.id] || 0) > 0);
          if (!relevant.length) return null;
          return (
            <div key={ti} style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", marginBottom: 5, padding: "7px 9px", background: "rgba(255,255,255,0.02)", borderRadius: 6, border: `1px solid rgba(255,136,50,${0.07 + ti * 0.05})` }}>
              {relevant.map(bm => (
                <div key={bm.id} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 12, opacity: (materials[bm.id] || 0) > 0 ? 1 : 0.25 }}>
                  <span style={{ fontSize: 13 }}>{bm.icon}</span>
                  <span style={{ color: bm.color, fontWeight: 700 }}>{fmt(materials[bm.id] || 0)}</span>
                  <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>{bm.name}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* ── Queue panel: active slot + pending slots ── */}
      <div style={{ margin: "0 14px 6px", flexShrink: 0 }}>

        {/* Queue counter (top-right) */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: queueFull ? "#ff7744" : "rgba(255,255,255,0.35)", fontWeight: 700 }}>
            Queue {smeltQueue.length}/{queueMax ?? 1}{queueFull ? " — FULL" : ""}
          </span>
        </div>

        {/* Active forge slot with progress bars */}
        {smeltQueue.length > 0 && (
          <div style={{ padding: "9px 11px", background: "rgba(255,136,50,0.07)", borderRadius: 8, border: "1px solid rgba(255,136,50,0.18)", marginBottom: 5 }}>

            {/* Recipe name + run counter */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
              <span style={{ fontSize: 13, color: "#ff9944", fontWeight: 700 }}>
                🔥 {frontRec?.name}{isBatch ? ` ×${totalRuns}` : ""}
              </span>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                {isBatch ? `run ${runsDone + 1}/${totalRuns} · ` : ""}{Math.floor(smeltProgress)}%
              </span>
            </div>

            {/* Per-run progress bar (orange) */}
            <div style={{ height: 5, background: "rgba(255,255,255,0.06)", borderRadius: 3 }}>
              <div style={{ height: "100%", width: `${smeltProgress}%`, background: "linear-gradient(90deg,#ff6622,#ffaa44)", borderRadius: 3, transition: "width 0.05s" }} />
            </div>

            {/* Overall batch progress bar (dim amber) — only shown for batches */}
            {isBatch && (
              <div style={{ marginTop: 3, height: 3, background: "rgba(255,255,255,0.04)", borderRadius: 2 }}>
                <div style={{ height: "100%", width: `${(runsDone / totalRuns) * 100}%`, background: "rgba(255,160,80,0.4)", borderRadius: 2, transition: "width 0.2s" }} />
              </div>
            )}
          </div>
        )}

        {/* Pending queue slots (index 1+) — tap to remove and refund */}
        {smeltQueue.length > 1 && (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {smeltQueue.slice(1).map((qi, idx) => {
                const rec = SMELT_RECIPES.find(r => r.id === qi.recipeId);
                const om  = BASE_MATERIALS.find(m => m.id === rec?.output);
                const runs = qi.count ?? 1;
                return (
                  <div key={qi.uid} onClick={() => removeFromQueue(idx + 1)}
                    title="Tap to remove and refund"
                    style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,136,50,0.15)", borderRadius: 5, cursor: "pointer", fontSize: 11, color: "rgba(255,255,255,0.65)" }}>
                    <span style={{ color: "rgba(255,255,255,0.35)" }}>#{idx + 1}</span>
                    <span style={{ color: om?.color }}>{om?.icon}</span>
                    <span style={{ color: "rgba(255,160,80,0.8)" }}>{rec?.name}{runs > 1 ? ` ×${runs}` : ""}</span>
                    <span style={{ color: "rgba(255,80,80,0.6)", fontSize: 10 }}>✕</span>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginTop: 3 }}>Tap to remove &amp; refund</div>
          </>
        )}
      </div>

      {/* ── Recipe cards — scrollable list ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 14px 8px" }}>
        {groups.map(grp => (
          <div key={grp}>
            {/* Tier section header */}
            <div style={{
              fontSize: 10, letterSpacing: 1.3, fontWeight: 700,
              color: TIER_COLORS[grp] ?? "#aaa",
              borderBottom: `1px solid ${TIER_COLORS[grp] ?? "#aaa"}33`,
              paddingBottom: 5, marginTop: 10, marginBottom: 8,
            }}>
              {TIER_LABELS[grp] ?? `FORGE TIER ${grp}`}
            </div>

            {/* 3-column grid of compact recipe cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 4 }}>
              {visibleRecipes.filter(r => recipeGroup(r) === grp).map(recipe => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  inventory={inventory}
                  materials={materials}
                  activeBatch={activeBatch}
                  queueFull={queueFull}
                  onQueue={handleQueue}
                  costMult={costMult}
                  yieldMult={yieldMult}
                  discoveredMaterials={discoveredMaterials}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ── Batch size bar — sticky at the bottom, only shown when >1 option unlocked ── */}
      {batchOptions.length > 1 && (
        <div style={{
          flexShrink: 0,
          borderTop: "1px solid rgba(255,255,255,0.07)",
          padding: "8px 14px",
          background: "rgba(6,10,16,0.97)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", fontWeight: 700, letterSpacing: 0.8, flexShrink: 0 }}>BATCH</span>
          <div style={{ display: "flex", gap: 5, flex: 1 }}>
            {batchOptions.map(opt => {
              const active = activeBatch === opt.value;
              return (
                <button key={opt.value} onClick={() => setBatchSize(opt.value)}
                  style={{
                    flex: 1, padding: "7px 4px",
                    background: active ? "rgba(255,160,50,0.16)" : "rgba(255,255,255,0.03)",
                    border: `1px solid ${active ? "rgba(255,160,50,0.55)" : "rgba(255,255,255,0.09)"}`,
                    borderRadius: 7,
                    color: active ? "#ffaa33" : "rgba(255,255,255,0.4)",
                    fontFamily: "inherit", fontSize: 13, fontWeight: 700,
                    cursor: "pointer", transition: "all 0.15s",
                  }}>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
